import {
  autoUpdate,
  FloatingPortal,
  offset,
  shift,
  flip,
  useDismiss,
  useFloating,
  useFocus,
  useHover,
  useInteractions,
  useRole,
  type Placement,
} from '@floating-ui/react';
import { clsx } from 'clsx';
import { cloneElement, isValidElement, useState, type ReactElement, type ReactNode } from 'react';

export interface TooltipProps {
  content: ReactNode;
  placement?: Placement;
  delayOpen?: number;
  children: ReactElement;
  className?: string;
}

export function Tooltip({
  content,
  placement = 'top',
  delayOpen = 500,
  children,
  className,
}: TooltipProps) {
  const [open, setOpen] = useState(false);
  const { refs, floatingStyles, context } = useFloating({
    open,
    onOpenChange: setOpen,
    placement,
    middleware: [offset(6), flip(), shift({ padding: 8 })],
    whileElementsMounted: autoUpdate,
  });

  const hover = useHover(context, { delay: { open: delayOpen, close: 0 }, move: false });
  const focus = useFocus(context);
  const dismiss = useDismiss(context);
  const role = useRole(context, { role: 'tooltip' });

  const { getReferenceProps, getFloatingProps } = useInteractions([hover, focus, dismiss, role]);

  const trigger = isValidElement(children)
    ? cloneElement(children, {
        ref: refs.setReference,
        ...getReferenceProps(),
      } as Record<string, unknown>)
    : children;

  return (
    <>
      {trigger}
      <FloatingPortal>
        {open && (
          <div
            ref={refs.setFloating}
            style={floatingStyles}
            {...getFloatingProps()}
            className={clsx(
              'z-50 rounded-md border border-line bg-bg-elevated px-2.5 py-1.5 text-xs font-medium text-fg-primary shadow-lg',
              className,
            )}
          >
            {content}
          </div>
        )}
      </FloatingPortal>
    </>
  );
}
